import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Empty, Input, Popover, Select, Space, Table, Tag, Typography, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, DownloadOutlined, FolderOpenOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppColors } from '../../shared/theme';
import { findScrollParent } from '../../shared/lib/visibleBox';
import ProductThumb from '../../shared/ui/ProductThumb';
import HintLabel from '../../shared/ui/HintLabel';
import { exchangeXidFromKey,
  BUILTIN_PRESETS,
  COLUMNS,
  EDITED_PRESET_ID,
  QUICK_FILTERS,
  applyPreset,
  applyQuickFilters,
  cellTone,
  columnDef,
  columnWidth,
  deletePreset,
  formatCell,
  historyCsv,
  loadView,
  moveColumn,
  saveAsPreset,
  sortValue,
  storeView,
  toggleColumn,
  toggleWide,
  visibleColumns,
  type HistoryRow,
  type QuickFilterId,
  type ViewState,
} from './historyTableLogic';

interface HistoryTableProps {
  rows: HistoryRow[];
  images: Record<string, string | null | undefined>;
  onLoad: (fileName: string) => void;
  /**
   * Адрес расчёта по id записи. Есть — имя становится настоящей ссылкой: открывается с
   * клавиатуры, средним кликом и в новой вкладке. Нет (standalone-шторка) — кнопкой-ссылкой.
   */
  resultHref?: (resultId: number) => string;
}

// Клик с модификатором или не левой кнопкой — браузер сам открывает ссылку (новая вкладка,
// окно); перехватывается только обычный клик, который грузит расчёт здесь же.
function isPlainClick(event: React.MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

const ASIN_COLUMN_WIDTH = 185;
const NAME_COLUMN_WIDTH = 170;
const ACTIONS_COLUMN_WIDTH = 150;
const THUMB_SIZE = 36;

const compare = (a: number | string | null, b: number | string | null): number => {
  // Пустые значения всегда внизу, в какую сторону ни сортируй.
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
};

/**
 * Таблица истории: колонки метрик, быстрые фильтры, «Колонки» (видимость,
 * порядок, ширина) и пресеты вида. Логика — в `historyTableLogic.ts`.
 */
const HistoryTable: React.FC<HistoryTableProps> = ({ rows, images, onLoad, resultHref }) => {
  const { t, i18n } = useTranslation();
  const c = useAppColors();
  const { token } = theme.useToken();
  const tableBoxRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<ViewState>(() => loadView());
  const [filters, setFilters] = useState<QuickFilterId[]>([]);
  const [presetName, setPresetName] = useState('');

  useEffect(() => storeView(view), [view]);

  const shown = visibleColumns(view);
  const filtered = useMemo(() => applyQuickFilters(rows, filters), [rows, filters]);
  const tone = (kind: 'bad' | 'plain') => (kind === 'bad' ? c.error : undefined);

  const columns: ColumnsType<HistoryRow> = [
    {
      key: 'asin',
      title: t('idea_calc.history.table.columns.asin'),
      fixed: 'left',
      // Фото + 10 знаков ASIN жирным крупным шрифтом страницы расчёта (≈181 px с
      // отступами): при 170 ASIN переносился по последнему символу («B08QMNLTF / 4»).
      width: ASIN_COLUMN_WIDTH,
      sorter: (a, b) => a.asin.localeCompare(b.asin),
      render: (_, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ProductThumb src={images[row.asin] ?? undefined} size={THUMB_SIZE} alt={row.asin} />
          <Typography.Text strong style={{ whiteSpace: 'nowrap' }}>{exchangeXidFromKey(row.asin) ? t('idea_calc.exchange.card_tag', { xid: exchangeXidFromKey(row.asin) }) : row.asin}</Typography.Text>
        </span>
      ),
    },
    {
      key: 'name',
      title: t('idea_calc.history.table.columns.name'),
      fixed: 'left',
      // Длинное имя переносится, а не обрезается многоточием: в авто-раскладке таблицы
      // ячейка без переноса растягивала колонку по самому длинному имени.
      width: NAME_COLUMN_WIDTH,
      // Имя открывает расчёт: колонка закреплена слева и видна на любой ширине, тогда как
      // кнопка «Загрузить» справа на узком экране уезжает за прокрутку.
      render: (_, row) =>
        resultHref && row.id !== undefined ? (
          <Typography.Link
            href={resultHref(row.id)}
            onClick={(event) => {
              if (!isPlainClick(event)) return;
              event.preventDefault();
              onLoad(row.fileName);
            }}
          >
            {row.fileName}
          </Typography.Link>
        ) : (
          <Button type="link" size="small" style={{ padding: 0, height: 'auto', whiteSpace: 'normal', textAlign: 'left' }} onClick={() => onLoad(row.fileName)}>
            {row.fileName}
          </Button>
        ),
    },
    ...shown.map((key) => {
      const kind = columnDef(key).kind;
      const numeric = kind === 'money' || kind === 'percent' || kind === 'count';
      return {
        key,
        title: (
          <HintLabel hint={t(`idea_calc.history.table.tips.${key}`)}>
            {t(`idea_calc.history.table.columns.${columnDef(key).labelKey}`)}
          </HintLabel>
        ),
        width: columnWidth(key, view.wide.includes(key)),
        align: numeric ? ('right' as const) : ('left' as const),
        sorter: (a: HistoryRow, b: HistoryRow) => compare(sortValue(a, key), sortValue(b, key)),
        render: (_: unknown, row: HistoryRow) => {
          if (key === 'source' && row.source === 'mcp') {
            return <Tag color="purple">{formatCell(row, key, i18n.language)}</Tag>;
          }
          return <span style={{ color: tone(cellTone(row, key)) }}>{formatCell(row, key, i18n.language)}</span>;
        },
      };
    }),
    {
      key: 'actions',
      // Не закреплена: закреплённая справа, она на узком экране (1366 px) закрывала
      // «Маржу» и ROI целиком (QA 09.10, WS 15587410). Открыть расчёт без прокрутки можно
      // по его имени слева. Ширина — по кнопке с иконкой крупным шрифтом страницы.
      width: ACTIONS_COLUMN_WIDTH,
      render: (_, row) => (
        <Button size="small" type="primary" ghost icon={<FolderOpenOutlined />} onClick={() => onLoad(row.fileName)}>
          {t('idea_calc.history.load_button')}
        </Button>
      ),
    },
  ];

  const presetOptions = [
    ...BUILTIN_PRESETS.map((p) => ({ value: p.id, label: t(`idea_calc.history.table.presets.${p.labelKey}`) })),
    ...view.custom.map((p) => ({ value: p.id, label: p.name ?? p.id })),
    ...(view.presetId === EDITED_PRESET_ID ? [{ value: EDITED_PRESET_ID, label: t('idea_calc.history.table.presets.edited') }] : []),
  ];
  const activeCustom = view.custom.find((p) => p.id === view.presetId);

  // Выгрузка — то, что видно: отфильтрованные строки, видимые колонки в их порядке.
  const exportCsv = () => {
    const header = [
      t('idea_calc.history.table.columns.asin'),
      t('idea_calc.history.table.columns.name'),
      ...shown.map((key) => t(`idea_calc.history.table.columns.${columnDef(key).labelKey}`)),
    ];
    // BOM — чтобы Excel узнал UTF-8 и не исказил кириллицу в именах расчётов.
    const blob = new Blob(['\uFEFF' + historyCsv(filtered, shown, header)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `idea-calc-history-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columnsPanel = (
    <div style={{ width: 460, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('idea_calc.history.table.columns_hint')}
      </Typography.Text>
      <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {view.order.map((key) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px', borderRadius: token.borderRadiusSM, background: view.visible.includes(key) ? token.colorFillQuaternary : undefined }}>
            <Checkbox checked={view.visible.includes(key)} onChange={() => setView((v) => toggleColumn(v, key))} style={{ flexGrow: 1 }}>
              {t(`idea_calc.history.table.columns.${columnDef(key).labelKey}`)}
            </Checkbox>
            <Button size="small" type="text" icon={<ArrowUpOutlined />} aria-label={t('idea_calc.history.table.move_up')} onClick={() => setView((v) => moveColumn(v, key, -1))} />
            <Button size="small" type="text" icon={<ArrowDownOutlined />} aria-label={t('idea_calc.history.table.move_down')} onClick={() => setView((v) => moveColumn(v, key, 1))} />
            <Button size="small" onClick={() => setView((v) => toggleWide(v, key))} style={{ width: 84 }}>
              {view.wide.includes(key) ? t('idea_calc.history.table.width_wide') : t('idea_calc.history.table.width_narrow')}
            </Button>
          </div>
        ))}
      </div>
      <Space.Compact>
        <Input
          placeholder={t('idea_calc.history.table.preset_name_placeholder')}
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          onPressEnter={() => {
            setView((v) => saveAsPreset(v, presetName));
            setPresetName('');
          }}
        />
        <Button
          type="primary"
          disabled={!presetName.trim()}
          onClick={() => {
            setView((v) => saveAsPreset(v, presetName));
            setPresetName('');
          }}
        >
          {t('idea_calc.history.table.save_preset')}
        </Button>
      </Space.Compact>
    </div>
  );

  return (
    <div ref={tableBoxRef} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Typography.Text type="secondary">{t('idea_calc.history.table.view_label')}</Typography.Text>
        <Select
          style={{ minWidth: 200 }}
          value={view.presetId}
          options={presetOptions}
          onChange={(id: string) => setView((v) => applyPreset(v, id))}
        />
        {activeCustom && (
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setView((v) => deletePreset(v, activeCustom.id))}>
            {t('idea_calc.history.table.delete_preset')}
          </Button>
        )}
        <Popover trigger="click" placement="bottomLeft" content={columnsPanel} title={t('idea_calc.history.table.columns_title')}>
          <Button icon={<SettingOutlined />}>
            {t('idea_calc.history.table.columns_button', { shown: shown.length + 2, total: COLUMNS.length + 2 })}
          </Button>
        </Popover>
        <Button icon={<DownloadOutlined />} disabled={filtered.length === 0} onClick={exportCsv}>
          {t('idea_calc.history.table.export_button')}
        </Button>
        <div style={{ flexGrow: 1 }} />
        <Typography.Text type="secondary">
          {t('idea_calc.history.table.counter', { shown: filtered.length, total: rows.length })}
        </Typography.Text>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {QUICK_FILTERS.map((f) => {
          const on = filters.includes(f.id);
          return (
            <Tag.CheckableTag
              key={f.id}
              checked={on}
              onChange={() => setFilters((prev) => (on ? prev.filter((x) => x !== f.id) : [...prev, f.id]))}
              // Цвет текста явно: у невыбранного CheckableTag он из светлой темы и в тёмной сливается с фоном.
              style={{ padding: '4px 12px', fontSize: 13, border: `1px solid ${on ? token.colorPrimary : token.colorBorder}`, color: on ? undefined : token.colorText }}
            >
              {t(`idea_calc.history.table.filters.${f.id}`)}
            </Tag.CheckableTag>
          );
        })}
        {filters.length > 0 && (
          <Button size="small" type="link" onClick={() => setFilters([])}>
            {t('idea_calc.history.table.filters_reset')}
          </Button>
        )}
      </div>

      <Table<HistoryRow>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 50, showSizeChanger: false, hideOnSinglePage: true }}
        scroll={{ x: 'max-content' }}
        // Подсказка сортировки — только на стрелках: на весь заголовок она ложилась
        // поверх подсказки значка ⓘ (QA 14.10, WS 15587410).
        showSorterTooltip={{ target: 'sorter-icon' }}
        // Полоса горизонтальной прокрутки липнет к низу видимой области, пока таблица на
        // экране: своя полоса таблицы стоит под 50-й строкой, и на узком экране (1366 px)
        // о скрытых справа «Марже» и ROI было не догадаться (QA 09.10, WS 15587410).
        // Прокручивает страницу платформы не окно, а её контейнер — его и слушаем.
        sticky={{ getContainer: () => (tableBoxRef.current && findScrollParent(tableBoxRef.current)) ?? window }}
        locale={{ emptyText: <Empty description={t('idea_calc.history.table.no_rows')} /> }}
      />
    </div>
  );
};

export default HistoryTable;
